import deepDiff, { diff } from 'deep-diff'

export function countDecimals(num: number) {
    if (!num || isNaN(num)) {
        return 0
    }
    if (Math.floor(num.valueOf()) === num.valueOf()) return 0
    return num.toString().split('.')[1].length || 0
}

export function getPropertiesHavingValue(obj: object, value: any) {
    return Object.keys(obj).filter((key) => obj[key as keyof typeof obj] === value)
}

export function roundToRange(value: number | null, rangeMin: number, rangeMax: number) {
    if (!value) {
        return rangeMin
    }
    // Check if value is less than x or greater than y
    if (value < rangeMin) {
        return rangeMin
    } else if (value > rangeMax) {
        return rangeMax
    }

    // Round value to the nearest whole number
    const roundedValue = Math.round(value)

    // Check if the rounded value is closer to x or y
    if (Math.abs(roundedValue - rangeMin) < Math.abs(roundedValue - rangeMax)) {
        return rangeMin
    } else {
        return rangeMax
    }
}

export function getNumberRange(start: number, end: number, step = 1, numberOfDecimals = 1) {
    if (typeof start !== 'number' || typeof end !== 'number' || typeof step !== 'number' || start > end || step <= 0) {
        return []
    }

    const result = []
    for (let i = start; parseFloat(i.toFixed(numberOfDecimals)) <= end; i += step) {
        result.push(parseFloat(i.toFixed(numberOfDecimals)))
    }

    return result
}

export function firstLetterOnlyCapital(str: string | null | undefined) {
    if (str == null) return null
    return str.toLowerCase().charAt(0).toUpperCase() + str.slice(1)
}

export function findNestedObjects(obj: object, predicate: (item: object) => boolean) {
    const result: any = []

    function traverse(obj: any) {
        for (const key in obj) {
            // eslint-disable-next-line no-prototype-builtins
            if (obj.hasOwnProperty(key)) {
                if (predicate(obj[key])) {
                    result.push(obj)
                } else if (typeof obj[key] === 'object') {
                    traverse(obj[key])
                }
            }
        }
    }

    traverse(obj)
    return result
}

export function difference<T>(object: T, other: T): deepDiff.Diff<T, T>[] {
    return diff(object, other) as any
}

export function delayAsync(delayms: number) {
    return new Promise(function (resolve) {
        setTimeout(resolve, delayms)
    })
}

export function getHash(str: string) {
    let hash = 0,
        i,
        chr
    if (str.length === 0) return hash
    for (i = 0; i < str.length; i++) {
        chr = str.charCodeAt(i)
        hash = (hash << 5) - hash + chr
        hash |= 0 // Convert to 32bit integer
    }
    return hash
}

export function isValidGUID(guid?: string) {
    if (!guid) {
        return false
    }
    const regex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/
    return regex.test(guid)
}

export function removeFalsyValues(obj: any) {
    // Iterate over the properties of the object
    for (const key in obj) {
        // Check if the property value is falsy
        if (!obj[key]) {
            // Delete the property if the value is falsy
            delete obj[key]
        }
    }
    return obj
}

export function findIndices<T>(array: T[], callback: (element: T) => boolean): number[] {
    const indices: number[] = []
    array.forEach((element, index) => {
        if (callback(element)) {
            indices.push(index)
        }
    })
    return indices
}

export function generateRandomCode(length = 8) {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
    let result = ''
    const charactersLength = characters.length
    for (let i = 0; i < length; i++) {
        result += characters.charAt(Math.floor(Math.random() * charactersLength))
    }
    return result
}

export function getRandomNumber(min: number, max: number) {
    return Math.floor(Math.random() * (max - min + 1)) + min
}

export function omitDeep(obj: any, keyToOmit: string): any {
    if (Array.isArray(obj)) {
        return obj.map((item) => omitDeep(item, keyToOmit))
    } else if (obj && typeof obj === 'object' && obj.constructor === Object) {
        return Object.fromEntries(
            Object.entries(obj)
                .filter(([k]) => k !== keyToOmit)
                .map(([k, v]) => [k, omitDeep(v, keyToOmit)])
        )
    }
    return obj
}
